import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import { trackBookDemoClick } from '../utils/analytics';
import MeetingEmbed from './MeetingEmbed';

/**
 * Inline-embedded Hubspot booking modal. Single CTA flow — all leads
 * funnel through Jon's calendar. No self-serve path.
 */
export default function LaunchCampaignChoiceModal({ open, onClose, source = 'home_hero', variant = 'pre_signup' }) {
  // Close on Esc + lock scroll while open. Also fire a single conversion
  // event when the modal opens (the user actively chose to start booking).
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    try { trackBookDemoClick({ source, variant }); } catch (_e) {}
    return () => {
      document.removeEventListener('keydown', handler);
      document.body.style.overflow = prev;
    };
  }, [open, onClose, source, variant]);

  if (!open) return null;

  const isPostSignup = variant === 'post_signup';

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-2 sm:p-4 bg-black/85 backdrop-blur-sm animate-fade-in-up"
      onClick={onClose}
      data-testid="launch-choice-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="launch-choice-title"
    >
      <div
        className="relative bg-[#111827] border border-white/10 max-w-4xl w-full max-h-[95vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="h-1 bg-[#FF4343]" />
        <button
          onClick={onClose}
          aria-label="Close"
          data-testid="launch-choice-close"
          className="sticky top-3 float-right mr-3 w-9 h-9 flex items-center justify-center text-[#9CA3AF] hover:text-white bg-[#111827]/80 hover:bg-white/5 transition-colors z-10"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="px-4 sm:px-8 pt-6 pb-2 text-center">
          <p className="text-[10px] sm:text-xs font-mono uppercase tracking-[0.3em] text-[#FF4343] mb-2">
            ★ {isPostSignup ? "One last thing" : "Let's get you matched"} ★
          </p>
          <h2 id="launch-choice-title" className="font-display text-2xl sm:text-4xl font-bold tracking-tight mb-2 leading-tight">
            {isPostSignup ? (
              <>Book your <span className="text-[#FF4343]">campaign kick-off</span> call</>
            ) : (
              <>Book a <span className="text-[#FF4343]">20-min walkthrough</span> with Jon</>
            )}
          </h2>
          <p className="text-xs sm:text-sm text-[#9CA3AF] leading-relaxed max-w-2xl mx-auto mb-4">
            {isPostSignup
              ? "Your account is created — now let's lock in your campaign. Jon will help you pick the right offering, write the brief, and pre-vet your first execs."
              : "Jon Fred (Founder) personally handles every campaign. He'll help you pick the right offering, write the brief, and pre-vet your first execs. Free. No pitch."}
          </p>
        </div>

        {/* Hubspot embedded calendar */}
        <div className="px-2 sm:px-4 pb-4">
          <MeetingEmbed minHeight={700} />
        </div>

        <p className="px-4 pb-4 text-[10px] text-center text-[#6B7280]">
          Prefer email? <a href="mailto:jon@freddymedia.com" className="text-[#FF4343] hover:underline">jon@freddymedia.com</a>
        </p>
      </div>
    </div>
  );
}
